import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Leaf, Shield, Users, CheckCircle, Recycle, Globe, Award, Heart } from "lucide-react";

const values = [
  {
    icon: Leaf,
    title: "Engagement environnemental",
    description: "Nous nous engageons à proposer des solutions d'emballage qui minimisent l'impact environnemental. Tous nos produits sont conçus pour être recyclables ou compostables.",
    details: [
      "Matières premières issues de sources durables",
      "Réduction continue de notre empreinte carbone",
      "Partenariats avec des fournisseurs responsables",
    ],
    color: "eco",
  },
  {
    icon: Shield,
    title: "Qualité & Sécurité alimentaire",
    description: "La sécurité de vos consommateurs est notre priorité. Tous nos emballages sont conformes aux réglementations européennes les plus strictes.",
    details: [
      "Conformité aux normes européennes",
      "Tests réguliers de migration",
      "Traçabilité complète des matériaux",
    ],
    color: "primary",
  },
  {
    icon: Users,
    title: "Service client d'excellence",
    description: "Notre équipe est à votre écoute pour vous accompagner dans le choix des meilleures solutions d'emballage adaptées à vos besoins.",
    details: [
      "Conseils personnalisés",
      "Réactivité et disponibilité",
      "Suivi de commande dédié",
    ],
    color: "accent",
  },
  {
    icon: CheckCircle,
    title: "Contrôle qualité strict",
    description: "Chaque lot de production est soumis à un contrôle qualité rigoureux effectué par un organisme certificateur indépendant.",
    details: [
      "Contrôles avant, pendant et après production",
      "Audits réguliers",
      "Certification ISO 9001:2015",
    ],
    color: "primary",
  },
  {
    icon: Recycle,
    title: "Durabilité",
    description: "Nous concevons des emballages qui s'inscrivent dans une économie circulaire, favorisant le recyclage et la réutilisation.",
    details: [
      "Matériaux recyclables",
      "Options compostables",
      "Réduction des déchets",
    ],
    color: "eco",
  },
  {
    icon: Globe,
    title: "Responsabilité sociale",
    description: "Notre engagement dépasse l'environnement. Nous veillons à des pratiques éthiques tout au long de notre chaîne d'approvisionnement.",
    details: [
      "Conditions de travail équitables",
      "Fournisseurs certifiés",
      "Commerce responsable",
    ],
    color: "accent",
  },
];

const ValueCard = ({ value, index }: { value: typeof values[0]; index: number }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      className="bg-card rounded-2xl border border-border p-8 hover:border-primary/20 transition-all duration-500 hover-float"
    >
      <div className={`w-16 h-16 rounded-2xl bg-${value.color}/10 flex items-center justify-center mb-6`}>
        <value.icon className={`w-8 h-8 text-${value.color}`} />
      </div>

      <h3 className="font-display text-2xl font-bold text-foreground mb-4">
        {value.title}
      </h3>

      <p className="text-muted-foreground mb-6 leading-relaxed">
        {value.description}
      </p>

      <ul className="space-y-3">
        {value.details.map((detail, i) => (
          <li key={i} className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-eco flex-shrink-0 mt-0.5" />
            <span className="text-sm text-foreground">{detail}</span>
          </li>
        ))}
      </ul>
    </motion.div>
  );
};

const Valeurs = () => {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        {/* Hero */}
        <section className="pt-32 pb-20 bg-gradient-to-b from-secondary/50 to-background relative overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute top-20 right-10 w-64 h-64 rounded-full bg-eco/10 blur-3xl" />
            <div className="absolute bottom-10 left-10 w-80 h-80 rounded-full bg-primary/10 blur-3xl" />
          </div>

          <div className="container mx-auto px-4 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-3xl mx-auto text-center"
            >
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-eco/10 text-eco rounded-full text-sm font-medium mb-6">
                <Heart className="w-4 h-4" />
                Nos engagements
              </span>
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">
                Nos Valeurs & Engagements
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Chez AMS DISTRIB, nous croyons qu'il est possible de proposer des emballages 
                de qualité tout en respectant l'environnement. Nos valeurs guident chacune 
                de nos décisions.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Values Grid */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {values.map((value, index) => (
                <ValueCard key={value.title} value={value} index={index} />
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-secondary/30">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="max-w-3xl mx-auto text-center"
            >
              <Award className="w-16 h-16 text-primary mx-auto mb-6" />
              <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-6">
                Notre engagement qualité
              </h2>
              <p className="text-muted-foreground mb-8 leading-relaxed">
                Certifiés ISO 9001:2015, nous nous engageons dans une démarche d'amélioration 
                continue. Chaque produit que nous livrons reflète notre exigence de qualité 
                et notre respect pour l'environnement.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <span className="px-6 py-3 bg-eco/10 text-eco rounded-full font-medium">
                  Certifié FSC®
                </span>
                <span className="px-6 py-3 bg-primary/10 text-primary rounded-full font-medium">
                  ISO 9001:2015
                </span>
                <span className="px-6 py-3 bg-accent/10 text-accent rounded-full font-medium">
                  Normes UE Alimentaires
                </span>
              </div>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Valeurs;